import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import AuditorBusinessPanel from './AuditorBusinessPanel';
import AuditorCommandNav from './AuditorCommandNav';
import SecurityPanel from './SecurityPanel';
import {
  AGENT_SLUG_TO_ID,
  AUDITOR_TABS,
  agentIdFromSlug,
  auditorPath,
  slugFromAgentId,
} from './auditorAgentRoutes';
import './AuditorModal.css';

/**
 * First-class Auditor page at /opo/auditor/:tab?/:agent?
 * Tactical hero + KPI strip + Business | Code | Security tabs.
 */

function complianceLevel(pct) {
  if (pct == null) return 'unknown';
  if (pct >= 80) return 'good';
  if (pct >= 50) return 'warning';
  return 'critical';
}

function deriveHero(compliance, gstSummary, t2Summary, tasksFlat) {
  const gstDays = gstSummary?.daysToDeadline;
  const gstOverdue = typeof gstDays === 'number' && gstDays < 0;
  const gstSoon = typeof gstDays === 'number' && gstDays >= 0 && gstDays <= 45;
  const needsAttention = (tasksFlat || []).some((t) => t.status === 'needs_attention');
  const pct = compliance?.summary?.compliancePercent;

  let level = 'good';
  let title = 'RECORDS IN ORDER';
  if (gstOverdue || needsAttention) {
    level = 'critical';
    title = 'FILING OVERDUE';
    if (!gstOverdue && needsAttention) title = 'ACTION NEEDED';
  } else if (gstSoon || (pct != null && pct < 80 && pct >= 50)) {
    level = 'warning';
    title = 'ACTION NEEDED';
  } else if (pct != null && pct < 50) {
    level = 'critical';
    title = 'ACTION NEEDED';
  }

  const bits = [];
  if (pct != null) bits.push(`${pct}% compliance`);
  if (gstDays != null) {
    bits.push(
      gstDays < 0
        ? `GST ${Math.abs(gstDays)}d overdue`
        : `GST due in ${gstDays}d`
    );
  }
  const openTasks = (tasksFlat || []).filter((t) => t.status !== 'complete').length;
  if (openTasks) bits.push(`${openTasks} open tasks`);
  if (needsAttention) bits.push('needs attention');

  return { level, title, sub: bits.join(' · ') || 'Auditor command surface' };
}

function flattenTasks(byArea) {
  if (!byArea || typeof byArea !== 'object') return [];
  return Object.values(byArea).flat().filter(Boolean);
}

export default function AuditorPage() {
  const navigate = useNavigate();
  const { tab: tabParam, agent: agentParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = AUDITOR_TABS.some((t) => t.id === tabParam) ? tabParam : 'business';
  // Accept every slug known to AGENT_SLUG_TO_ID (incl. corporate-records, receipts,
  // assets, investor-readiness, and legacy corporate-secretary / compliance aliases).
  // A hardcoded subset here previously forced unknown slugs back to Command Center,
  // so the Resolutions workspace never mounted.
  const agentSlug = tab === 'business'
    ? (agentParam && Object.prototype.hasOwnProperty.call(AGENT_SLUG_TO_ID, agentParam)
        ? agentParam
        : 'command')
    : 'command';

  const [commandCollapsed, setCommandCollapsed] = useState(() => {
    try {
      return localStorage.getItem('auditor.commandNav.collapsed') === '1';
    } catch {
      return false;
    }
  });
  const onToggleCommandCollapsed = useCallback((next) => {
    setCommandCollapsed(next);
    try {
      localStorage.setItem('auditor.commandNav.collapsed', next ? '1' : '0');
    } catch { /* ignore */ }
  }, []);

  const coordinatorView = searchParams.get('view') || 'compliance';
  const setCoordinatorView = useCallback(
    (view) => {
      const next = new URLSearchParams(searchParams);
      if (!view || view === 'compliance') next.delete('view');
      else next.set('view', view);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Normalize incomplete URLs (e.g. /opo/auditor → /opo/auditor/business/command)
  useEffect(() => {
    if (!tabParam || (tab === 'business' && !agentParam)) {
      navigate(auditorPath(tab || 'business', agentSlug), { replace: true });
    }
  }, [tabParam, agentParam, tab, agentSlug, navigate]);

  const [compliance, setCompliance] = useState(null);
  const [gstSummary, setGstSummary] = useState(null);
  const [t2Summary, setT2Summary] = useState(null);
  const [tasksFlat, setTasksFlat] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchAll = useCallback(() => {
    setSyncing(true);
    Promise.all([
      fetch('/api/opo/auditor/compliance-scan').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch('/api/opo/gst-pst/summary').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch('/api/opo/t2/summary').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch('/api/opo/auditor/tasks').then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ])
      .then(([c, g, t, tasks]) => {
        setCompliance(c);
        setGstSummary(g);
        setT2Summary(t);
        setTasksFlat(flattenTasks(tasks?.byArea));
        setSyncedAt(new Date());
      })
      .finally(() => setSyncing(false));
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const hero = useMemo(
    () => deriveHero(compliance, gstSummary, t2Summary, tasksFlat),
    [compliance, gstSummary, t2Summary, tasksFlat]
  );

  const compliancePct = compliance?.summary?.compliancePercent;
  const gstDays = gstSummary?.daysToDeadline;
  const t2Status =
    t2Summary?.lastFiled?.status ||
    t2Summary?.currentPeriod?.status ||
    t2Summary?.proactive?.status ||
    '—';
  const openTasks = tasksFlat.filter((t) => t.status !== 'complete').length;
  const needsAttention = tasksFlat.filter((t) => t.status === 'needs_attention').length;

  const gstKpiLevel =
    gstDays == null ? 'unknown' : gstDays < 0 ? 'critical' : gstDays <= 45 ? 'warning' : 'good';
  const tasksKpiLevel = needsAttention > 0 ? 'critical' : openTasks > 10 ? 'warning' : 'good';

  const goTab = (nextTab) => {
    if (nextTab === 'business') navigate(auditorPath('business', agentSlug));
    else navigate(auditorPath(nextTab));
  };

  const goAgent = (slug) => {
    navigate(auditorPath('business', slug));
  };

  return (
    <div className="auditor-shell opo-tactical">
      <div className={`opo-hero opo-hero-${hero.level}`}>
        <div className="opo-hero-scan" aria-hidden="true" />
        <div className="opo-hero-left">
          <div className="opo-hero-eyebrow">AUDITOR OPERATIONS · OPTIPRIME AI INC</div>
          <div className="opo-hero-title">
            <span className={`opo-hero-pulse opo-pulse-${hero.level}`} />
            <h2>{hero.title}</h2>
          </div>
          <div className="opo-hero-sub">{hero.sub}</div>
        </div>
        <div className="opo-hero-right">
          <button
            type="button"
            className={`opo-refresh-btn${syncing ? ' is-busy' : ''}`}
            onClick={fetchAll}
            disabled={syncing}
          >
            <span className="opo-refresh-icon" /> SYNC
          </button>
          <div className="opo-hero-timestamp">
            {syncedAt ? syncedAt.toLocaleString() : '—'}
          </div>
        </div>
      </div>

      <div className="opo-kpi-row">
        <div className={`opo-kpi opo-card-${complianceLevel(compliancePct)}`}>
          <div className="opo-kpi-label">DOCUMENT COMPLIANCE</div>
          <div className="opo-kpi-value">
            {compliancePct != null ? `${compliancePct}%` : '—'}
          </div>
          <div className="opo-kpi-foot">
            {compliance?.summary
              ? `${compliance.summary.present}/${compliance.summary.total} on file`
              : 'scan pending'}
          </div>
        </div>
        <div className={`opo-kpi opo-card-${gstKpiLevel}`}>
          <div className="opo-kpi-label">GST DEADLINE</div>
          <div className="opo-kpi-value">
            {gstDays == null
              ? '—'
              : gstDays < 0
                ? `${Math.abs(gstDays)}d`
                : `${gstDays}d`}
          </div>
          <div className="opo-kpi-foot">
            {gstDays == null
              ? 'no summary'
              : gstDays < 0
                ? 'overdue'
                : gstDays === 0
                  ? 'due today'
                  : 'days remaining'}
          </div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">T2 STATUS</div>
          <div className="opo-kpi-value" style={{ fontSize: 20, textTransform: 'uppercase' }}>
            {String(t2Status).replace(/_/g, ' ')}
          </div>
          <div className="opo-kpi-foot">
            {t2Summary?.assessedCount != null
              ? `${t2Summary.assessedCount} assessed · ${t2Summary.filedCount ?? 0} filed`
              : 'corporate return'}
          </div>
        </div>
        <div className={`opo-kpi opo-card-${tasksKpiLevel}`}>
          <div className="opo-kpi-label">OPEN TASKS</div>
          <div className="opo-kpi-value">{openTasks}</div>
          <div className="opo-kpi-foot">
            {needsAttention > 0
              ? `${needsAttention} needs attention`
              : `${tasksFlat.length} total`}
          </div>
        </div>
      </div>

      <div className="auditor-shell-tabs" role="tablist" aria-label="Auditor domains">
        {AUDITOR_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`auditor-shell-tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => goTab(t.id)}
          >
            {t.id === 'security' ? '🛡 Security' : t.label}
          </button>
        ))}
      </div>

      <div className="auditor-shell-workspace">
        {tab === 'business' && (
          <div className="auditor-shell-business">
            <AuditorCommandNav
              activeSlug={agentSlug}
              onSelect={goAgent}
              collapsed={commandCollapsed}
              onToggleCollapsed={onToggleCommandCollapsed}
            />
            <div className="auditor-shell-panel">
              <AuditorBusinessPanel
                pageMode
                agentSlug={agentSlug}
                agentId={agentIdFromSlug(agentSlug)}
                onAgentNavigate={(id) => goAgent(slugFromAgentId(id))}
                coordinatorView={coordinatorView}
                onCoordinatorViewChange={setCoordinatorView}
                statusSnapshot={{
                  compliance,
                  gstSummary,
                  t2Summary,
                  tasksFlat,
                  onNavigateAgent: goAgent,
                  onRefresh: fetchAll,
                }}
              />
            </div>
          </div>
        )}
        {tab === 'code' && (
          <div className="auditor-code-empty">
            <div className="auditor-code-empty-eyebrow">CODE AUDIT</div>
            <h2 className="auditor-code-empty-title">CODE AUDIT NOT COMMISSIONED</h2>
            <p className="auditor-code-empty-hint">
              Agents that audit OptiPrime codebases will live here. Business and Security tabs remain available.
            </p>
          </div>
        )}
        {tab === 'security' && (
          <div className="auditor-shell-security">
            <SecurityPanel />
          </div>
        )}
      </div>
    </div>
  );
}
